import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ArrowRight, Maximize2, Plus, X } from "lucide-react";
import { useMemo, useState } from "react";
import { getDecisionTableColumns, getRuleInputValue } from "./decisionTableColumns";
import { ExternalContactCard } from "../external-contact-card/external-contact-card";
import { SystemCard } from "../system-card/system-card";
import { OrgFunctionCard } from "../org-function-card/org-function-card";
import { UserCard, toUserCardProps } from "../user-card/user-card";
import { resolveEntityRefDisplay } from "../step-detail/suggestionNames";
import { ProposedEntityNameLabel } from "../step-detail/ProposedEntityName";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../base/dialog/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../base/table/table";
// `TableBody`'s `[&_tr:last-child]:border-0` outranks a plain `border-l-2`.
const INVALID_RULE_CLASS = "data-invalid:border-l-2! data-invalid:border-l-destructive data-invalid:bg-destructive/10";
/** Persons/externalContacts entries arrive as plain id strings or populated
 *  objects (never union refs — orgFunctions/systems resolve via the shared
 *  resolver). */ function idOf(value) {
    if (!value) return null;
    if (typeof value === "string") return value;
    if (typeof value !== "object") return null;
    const ref = value;
    return ref._id ? String(ref._id) : null;
}
/**
 * Resolve a persons/externalContacts successor entry to a displayable entity.
 * The lookup wins over an already-populated entry: lookups are fetched with
 * `includeDeleted` and are the only source carrying the soft-delete tombstone
 * (backend serialization strips `deletedAt` from populated wire objects), so
 * preferring the populated object would silently drop the "(deleted)" marker.
 * Entries the lookup can't resolve (read-only surfaces that pass no lookups)
 * still fall back to the populated object so the cell renders.
 */ function resolveEntity(entry, lookup) {
    const id = idOf(entry);
    const fromLookup = id && lookup ? lookup.get(id) : undefined;
    if (fromLookup) return fromLookup;
    if (entry && typeof entry === "object" && "_id" in entry) {
        const obj = entry;
        const keys = Object.keys(obj).filter((k)=>k !== "_id");
        if (keys.length > 0) return obj;
    }
    return null;
}
function ruleHasSuccessors(rule) {
    const s = rule.successors;
    if (!s) return false;
    if (s.successorStatus === "NONE" || s.successorStatus === "UNCLEAR") {
        return true;
    }
    return (s.persons?.length ?? 0) + (s.orgFunctions?.length ?? 0) + (s.systems?.length ?? 0) + (s.externalContacts?.length ?? 0) > 0;
}
function MissingEntity({ id }) {
    return /*#__PURE__*/ _jsx("span", {
        className: "text-xs text-fg-subtle",
        children: id
    });
}
// A suggestion-typed successor: its proposed name (blue, or red once declined)
// once resolvable from the suggestion-info map, else the kind-specific
// pending placeholder.
function PendingSuccessorLabel({ row }) {
    if (row.kind === "pending") {
        return /*#__PURE__*/ _jsx("span", {
            className: "text-fg-subtle italic text-xs",
            children: row.label
        });
    }
    return /*#__PURE__*/ _jsx(ProposedEntityNameLabel, {
        name: row.label,
        isDeclined: row.isDeclined,
        className: "text-xs"
    });
}
function SuccessorEntityRow({ children, onRemove, removeLabel }) {
    return /*#__PURE__*/ _jsxs("div", {
        className: "group/successor flex items-center gap-1 min-w-0 rounded-sm py-px px-0.5 -mx-0.5 hover:bg-white/70",
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "min-w-0 flex-1",
                children: children
            }),
            onRemove && /*#__PURE__*/ _jsx("button", {
                type: "button",
                "aria-label": removeLabel,
                title: removeLabel,
                onClick: (e)=>{
                    e.stopPropagation();
                    onRemove();
                },
                className: "p-0.5 text-fg-subtle opacity-0 group-hover/successor:opacity-100 hover:text-danger shrink-0",
                children: /*#__PURE__*/ _jsx(X, {
                    className: "w-3 h-3"
                })
            })
        ]
    });
}
function RuleSuccessorEntities({ rule, ruleId, t, lookups, onRemoveRuleSuccessor, onClearRuleSuccessorStatus, suggestionInfoById }) {
    const removeLabel = t("processFlow.editable.removeSuccessor");
    const successors = rule.successors;
    if (!successors) return null;
    if (successors.successorStatus === "NONE" || successors.successorStatus === "UNCLEAR") {
        const label = successors.successorStatus === "NONE" ? t("processFlow.noSuccessors") : t("processFlow.unclearSuccessors");
        return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
            onRemove: ruleId && onClearRuleSuccessorStatus ? ()=>onClearRuleSuccessorStatus(ruleId) : undefined,
            removeLabel: removeLabel,
            children: /*#__PURE__*/ _jsx("span", {
                className: "text-fg-subtle italic text-xs",
                children: label
            })
        });
    }
    const remove = (type, entityId)=>ruleId && onRemoveRuleSuccessor ? ()=>onRemoveRuleSuccessor(ruleId, type, entityId) : undefined;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5",
        children: [
            (successors.persons ?? []).map((entry, i)=>{
                const id = idOf(entry);
                if (!id) return null;
                const user = resolveEntity(entry, lookups.users);
                return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                    onRemove: remove("persons", id),
                    removeLabel: removeLabel,
                    children: user ? /*#__PURE__*/ _jsx(UserCard, {
                        user: toUserCardProps(user),
                        variant: "name-only",
                        size: "sm",
                        className: "p-0"
                    }) : /*#__PURE__*/ _jsx(MissingEntity, {
                        id: id
                    })
                }, `p-${id}-${i}`);
            }),
            (successors.orgFunctions ?? []).map((entry, i)=>{
                const row = resolveEntityRefDisplay(entry, {
                    suggestionInfoById,
                    pendingLabel: t("processFlow.pendingOrgFunction"),
                    getEntity: (id)=>lookups.orgFunctions?.get(id),
                    t
                });
                if (row.kind === "proposed" || row.kind === "pending") {
                    return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                        removeLabel: removeLabel,
                        children: /*#__PURE__*/ _jsx(PendingSuccessorLabel, {
                            row: row
                        })
                    }, `t-pending-${i}`);
                }
                return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                    onRemove: remove("orgFunctions", entry.id),
                    removeLabel: removeLabel,
                    children: row.kind === "entity" ? /*#__PURE__*/ _jsx(OrgFunctionCard, {
                        orgFunction: row.entity,
                        variant: "name-only",
                        size: "sm",
                        className: "p-0"
                    }) : /*#__PURE__*/ _jsx(MissingEntity, {
                        id: row.label
                    })
                }, `t-${entry.id}-${i}`);
            }),
            (successors.systems ?? []).map((entry, i)=>{
                const row = resolveEntityRefDisplay(entry, {
                    suggestionInfoById,
                    pendingLabel: t("processFlow.pendingSystem"),
                    getEntity: (id)=>lookups.systems?.get(id),
                    t
                });
                if (row.kind === "proposed" || row.kind === "pending") {
                    return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                        removeLabel: removeLabel,
                        children: /*#__PURE__*/ _jsx(PendingSuccessorLabel, {
                            row: row
                        })
                    }, `s-pending-${i}`);
                }
                return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                    onRemove: remove("systems", entry.id),
                    removeLabel: removeLabel,
                    children: row.kind === "entity" ? /*#__PURE__*/ _jsx(SystemCard, {
                        system: row.entity,
                        variant: "name-only",
                        size: "sm",
                        className: "p-0"
                    }) : /*#__PURE__*/ _jsx(MissingEntity, {
                        id: row.label
                    })
                }, `s-${entry.id}-${i}`);
            }),
            (successors.externalContacts ?? []).map((entry, i)=>{
                const id = idOf(entry);
                if (!id) return null;
                const contact = resolveEntity(entry, lookups.externalContacts);
                return /*#__PURE__*/ _jsx(SuccessorEntityRow, {
                    onRemove: remove("externalContacts", id),
                    removeLabel: removeLabel,
                    children: contact ? /*#__PURE__*/ _jsx(ExternalContactCard, {
                        contact: contact,
                        variant: "name-only",
                        size: "sm",
                        className: "p-0"
                    }) : /*#__PURE__*/ _jsx(MissingEntity, {
                        id: id
                    })
                }, `e-${id}-${i}`);
            })
        ]
    });
}
function AddSuccessorButton({ onClick, label }) {
    return /*#__PURE__*/ _jsxs("button", {
        type: "button",
        onClick: onClick,
        className: "inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-fg-secondary rounded-sm px-0.5 -mx-0.5",
        children: [
            /*#__PURE__*/ _jsx(Plus, {
                className: "w-3 h-3"
            }),
            label
        ]
    });
}
function SuccessorsCell({ rule, onEdit, onRemoveRuleSuccessor, onClearRuleSuccessorStatus, lookups, t, suggestionInfoById }) {
    const hasContent = ruleHasSuccessors(rule);
    const ruleId = rule._id ? String(rule._id) : null;
    if (!onEdit || !ruleId) {
        if (!hasContent) return /*#__PURE__*/ _jsx("span", {
            className: "text-fg-subtle",
            children: "-"
        });
        return /*#__PURE__*/ _jsx(RuleSuccessorEntities, {
            rule: rule,
            ruleId: null,
            t: t,
            lookups: lookups,
            suggestionInfoById: suggestionInfoById
        });
    }
    if (!hasContent) {
        return /*#__PURE__*/ _jsx(AddSuccessorButton, {
            onClick: ()=>onEdit(ruleId),
            label: t("processFlow.editable.addSuccessor")
        });
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-0.5",
        children: [
            /*#__PURE__*/ _jsx(RuleSuccessorEntities, {
                rule: rule,
                ruleId: ruleId,
                t: t,
                lookups: lookups,
                onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                suggestionInfoById: suggestionInfoById
            }),
            /*#__PURE__*/ _jsx(AddSuccessorButton, {
                onClick: ()=>onEdit(ruleId),
                label: t("processFlow.editable.addSuccessor")
            })
        ]
    });
}
function CompactRulesTable({ columns, rules, t, onEditRuleSuccessors, onRemoveRuleSuccessor, onClearRuleSuccessorStatus, lookups, suggestionInfoById, invalidRuleIndexes }) {
    return /*#__PURE__*/ _jsxs("table", {
        className: "text-xs w-full",
        children: [
            /*#__PURE__*/ _jsx("thead", {
                children: /*#__PURE__*/ _jsxs("tr", {
                    className: "border-b border-line",
                    children: [
                        columns.map((col)=>/*#__PURE__*/ _jsx("th", {
                                className: "text-left font-medium text-muted-foreground uppercase tracking-wide text-3xs py-1 pr-3 whitespace-nowrap",
                                children: col
                            }, col)),
                        /*#__PURE__*/ _jsx("th", {
                            className: "text-left font-medium text-muted-foreground uppercase tracking-wide text-3xs py-1 pr-3 whitespace-nowrap",
                            children: t("assignmentReview.outputLabel")
                        }),
                        /*#__PURE__*/ _jsx("th", {
                            className: "text-left font-medium text-muted-foreground uppercase tracking-wide text-3xs py-1 whitespace-nowrap",
                            children: t("processFlow.successors")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx("tbody", {
                children: rules.map((rule, ruleIndex)=>/*#__PURE__*/ _jsxs("tr", {
                        "data-invalid": invalidRuleIndexes?.has(ruleIndex) || undefined,
                        className: `align-top border-b border-line last:border-b-0 ${INVALID_RULE_CLASS}`,
                        children: [
                            columns.map((col)=>/*#__PURE__*/ _jsx("td", {
                                    className: "text-fg-secondary py-1.5 pr-3 whitespace-nowrap",
                                    children: getRuleInputValue(rule.inputs, col) ?? /*#__PURE__*/ _jsx("span", {
                                        className: "text-fg-subtle",
                                        children: "-"
                                    })
                                }, col)),
                            /*#__PURE__*/ _jsx("td", {
                                className: "text-fg-secondary font-medium py-1.5 pr-3 whitespace-nowrap",
                                children: rule.output ?? /*#__PURE__*/ _jsx("span", {
                                    className: "text-fg-subtle",
                                    children: "-"
                                })
                            }),
                            /*#__PURE__*/ _jsx("td", {
                                className: "text-fg-secondary py-1.5",
                                children: /*#__PURE__*/ _jsx(SuccessorsCell, {
                                    rule: rule,
                                    onEdit: onEditRuleSuccessors,
                                    onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                                    onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                                    lookups: lookups,
                                    t: t,
                                    suggestionInfoById: suggestionInfoById
                                })
                            })
                        ]
                    }, rule._id || ruleIndex))
            })
        ]
    });
}
function ExpandedRulesTable({ columns, rules, t, onEditRuleSuccessors, onRemoveRuleSuccessor, onClearRuleSuccessorStatus, lookups, suggestionInfoById, invalidRuleIndexes }) {
    return /*#__PURE__*/ _jsxs(Table, {
        children: [
            /*#__PURE__*/ _jsx(TableHeader, {
                children: /*#__PURE__*/ _jsxs(TableRow, {
                    children: [
                        columns.map((col)=>/*#__PURE__*/ _jsx(TableHead, {
                                className: "whitespace-nowrap",
                                children: col
                            }, col)),
                        /*#__PURE__*/ _jsx(TableHead, {
                            className: "whitespace-nowrap",
                            children: t("assignmentReview.outputLabel")
                        }),
                        /*#__PURE__*/ _jsx(TableHead, {
                            className: "whitespace-nowrap",
                            children: t("processFlow.successors")
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsx(TableBody, {
                children: rules.map((rule, ruleIndex)=>/*#__PURE__*/ _jsxs(TableRow, {
                        "data-invalid": invalidRuleIndexes?.has(ruleIndex) || undefined,
                        className: INVALID_RULE_CLASS,
                        children: [
                            columns.map((col)=>/*#__PURE__*/ _jsx(TableCell, {
                                    className: "whitespace-nowrap",
                                    children: getRuleInputValue(rule.inputs, col) ?? /*#__PURE__*/ _jsx("span", {
                                        className: "text-fg-subtle",
                                        children: "-"
                                    })
                                }, col)),
                            /*#__PURE__*/ _jsx(TableCell, {
                                className: "whitespace-nowrap font-medium",
                                children: rule.output ?? /*#__PURE__*/ _jsx("span", {
                                    className: "text-fg-subtle",
                                    children: "-"
                                })
                            }),
                            /*#__PURE__*/ _jsx(TableCell, {
                                children: /*#__PURE__*/ _jsx(SuccessorsCell, {
                                    rule: rule,
                                    onEdit: onEditRuleSuccessors,
                                    onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                                    onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                                    lookups: lookups,
                                    t: t,
                                    suggestionInfoById: suggestionInfoById
                                })
                            })
                        ]
                    }, rule._id || ruleIndex))
            })
        ]
    });
}
export function DecisionRulesSection({ rules, t, onEditRuleSuccessors, onRemoveRuleSuccessor, onClearRuleSuccessorStatus, assigneeLookup, suggestionInfoById, invalidRuleIndexes }) {
    const [expanded, setExpanded] = useState(false);
    const lookups = useMemo(()=>({
            users: assigneeLookup?.users ?? null,
            orgFunctions: assigneeLookup?.orgFunctions ?? null,
            systems: assigneeLookup?.systems ?? null,
            externalContacts: assigneeLookup?.externalContacts ?? null
        }), [
        assigneeLookup
    ]);
    if (rules.length === 0) return null;
    const columns = getDecisionTableColumns(rules);
    const hasConditions = columns.length > 0;
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between mb-1",
                children: [
                    /*#__PURE__*/ _jsx("label", {
                        className: "text-xs font-medium text-muted-foreground",
                        children: t("processFlow.decisionRules")
                    }),
                    hasConditions && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        onClick: ()=>setExpanded(true),
                        className: "text-fg-subtle hover:text-fg-secondary transition-colors",
                        "aria-label": t("processFlow.expandTable"),
                        title: t("processFlow.expandTable"),
                        children: /*#__PURE__*/ _jsx(Maximize2, {
                            className: "w-3.5 h-3.5"
                        })
                    })
                ]
            }),
            hasConditions && /*#__PURE__*/ _jsx("div", {
                className: "bg-surface-subtle rounded-md p-2 overflow-x-auto",
                children: /*#__PURE__*/ _jsx(CompactRulesTable, {
                    columns: columns,
                    rules: rules,
                    t: t,
                    onEditRuleSuccessors: onEditRuleSuccessors,
                    onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                    onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                    lookups: lookups,
                    suggestionInfoById: suggestionInfoById,
                    invalidRuleIndexes: invalidRuleIndexes
                })
            }),
            !hasConditions && /*#__PURE__*/ _jsx("div", {
                className: "bg-surface-subtle rounded-md p-2 space-y-1.5",
                children: rules.map((rule, index)=>/*#__PURE__*/ _jsxs("div", {
                        "data-invalid": invalidRuleIndexes?.has(index) || undefined,
                        className: `flex items-start gap-1.5 text-xs data-invalid:rounded-sm data-invalid:pl-1 ${INVALID_RULE_CLASS}`,
                        children: [
                            /*#__PURE__*/ _jsx(ArrowRight, {
                                className: "h-3 w-3 text-highlight mt-0.5 shrink-0"
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-fg-secondary flex-1 min-w-0",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: rule.output
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "mt-0.5",
                                        children: /*#__PURE__*/ _jsx(SuccessorsCell, {
                                            rule: rule,
                                            onEdit: onEditRuleSuccessors,
                                            onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                                            onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                                            lookups: lookups,
                                            t: t,
                                            suggestionInfoById: suggestionInfoById
                                        })
                                    })
                                ]
                            })
                        ]
                    }, rule._id || index))
            }),
            hasConditions && /*#__PURE__*/ _jsx(Dialog, {
                open: expanded,
                onOpenChange: setExpanded,
                children: /*#__PURE__*/ _jsxs(DialogContent, {
                    className: "max-w-[85vw] max-h-[80vh] flex flex-col",
                    children: [
                        /*#__PURE__*/ _jsxs(DialogHeader, {
                            children: [
                                /*#__PURE__*/ _jsx(DialogTitle, {
                                    children: t("processFlow.decisionRules")
                                }),
                                /*#__PURE__*/ _jsx(DialogDescription, {
                                    className: "sr-only",
                                    children: t("processFlow.expandTable")
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            className: "flex-1 min-h-0 overflow-auto border border-line rounded-lg",
                            children: /*#__PURE__*/ _jsx(ExpandedRulesTable, {
                                columns: columns,
                                rules: rules,
                                t: t,
                                onEditRuleSuccessors: onEditRuleSuccessors,
                                onRemoveRuleSuccessor: onRemoveRuleSuccessor,
                                onClearRuleSuccessorStatus: onClearRuleSuccessorStatus,
                                lookups: lookups,
                                suggestionInfoById: suggestionInfoById,
                                invalidRuleIndexes: invalidRuleIndexes
                            })
                        })
                    ]
                })
            })
        ]
    });
}

//# sourceMappingURL=DecisionRulesSection.js.map